import { useNavigate } from '@tanstack/react-router'
import { Modal } from 'antd'
import CantileverButton from 'components/CantileverButton'
import {
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  FormFieldsByUniqueID,
  SimpleFormField,
} from 'components/form/formTypes'
import {
  CaseJSONFromFormFields,
  CreateCaseForm,
  CreateCaseFormFields,
  DEFAULT_CASE_STATUSES,
  DEFAULT_CASE_TYPES,
} from 'features/cases'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoNotifications } from 'features/notifications'
import { PermissionsGate } from 'features/permissions'
import {
  extractUniqueEntryIds,
  MentionPlugin,
  RichTextEditorLexical,
} from 'features/richText'
import { generateEntrySlug, useRolodexEntries } from 'features/rolodex'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { SEARCH_PARAM_CASE_ID } from 'routes/cases/-searchParams'
import styled from 'styled-components'
import {
  PERMISSION_CANTILEVER_CASES_EDITOR,
  RichTextContent,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const EditorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const EditorLabel = styled.label`
  font-weight: 500;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

const EditorContainer = styled.div`
  min-height: 200px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  padding: 0.5rem;
`

interface CreateCaseModalProps {
  isOpen: boolean
  onClose: () => void
  siteID: string
  organizationID: string
  initialCaseContent?: RichTextContent
  additionalEntriesToPatch?: RolodexEntryPatchJson[]
}

export const CreateCaseModal: React.FC<CreateCaseModalProps> = ({
  isOpen,
  onClose,
  siteID,
  organizationID,
  initialCaseContent,
  additionalEntriesToPatch,
}) => {
  const navigate = useNavigate()
  const { errorNotification, successNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()
  const caseFormRef = useRef<HTMLFormElement>(null)
  const [content, setContent] = useState<RichTextContent>('' as RichTextContent)
  const [caseID, setCaseID] = useState<string>('')

  const { siteConfigs } = useRolodexEntries({
    type: RolodexEntryType.SITE_CONFIG,
    tags: { site: siteID },
    limit: 1,
  })

  const siteConfig = useMemo(() => {
    return siteConfigs.at(0)
  }, [siteConfigs])

  const caseFormFields = useMemo<FormFieldsByUniqueID[]>(() => {
    const caseTypes =
      siteConfig?.data.caseConfig?.caseTypes ?? DEFAULT_CASE_TYPES
    const caseStatuses =
      siteConfig?.data.caseConfig?.caseStatuses ?? DEFAULT_CASE_STATUSES

    return [
      {
        uniqueID: 'caseForm',
        formFields: CreateCaseFormFields(
          caseTypes.map((type) => ({ label: type, value: type })),
          caseStatuses.map((status) => ({ label: status, value: status }))
        ),
      },
    ]
  }, [siteConfig])

  useEffect(() => {
    if (isOpen) {
      setCaseID(generateEntrySlug(`case-${Date.now()}`))
      setContent(initialCaseContent ?? ('' as RichTextContent))
    }
  }, [isOpen, initialCaseContent])

  const handleSaveCase = () => {
    if (caseFormRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      caseFormRef.current.dispatchEvent(event)
    }
  }

  const onSubmit: SubmitHandler<SimpleFormField> = async (data) => {
    try {
      const convertedFormFields = createEditedObject(
        data,
        createUniqueFormFields(caseFormFields)
      )
      const caseData = CaseJSONFromFormFields(convertedFormFields[0].formFields)

      // Extract entry IDs from rich text content (slash commands)
      const supplementaryLinks = extractUniqueEntryIds(content)

      const entriesToPatch: RolodexEntryPatchJson[] = [
        ...(additionalEntriesToPatch ?? []),
        {
          id: caseID,
          type: RolodexEntryType.CASE,
          data: {
            case_title: caseData.case_title ?? '',
            case_type: caseData.case_type ?? '',
            case_status: caseData.case_status ?? '',
            content: content,
            supplementary_links: supplementaryLinks,
            is_published: false,
            case_metadata: {},
          },
          tags: {
            site: siteID,
            organization: organizationID,
          },
        },
      ]

      await rolodexPatch(entriesToPatch)

      successNotification('Case created successfully!')
      onClose()

      navigate({
        to: '/cases/details',
        search: (prev) => ({
          ...prev,
          [SEARCH_PARAM_SITE_ID]: siteID,
          [SEARCH_PARAM_CASE_ID]: caseID,
        }),
      })
    } catch (error) {
      errorNotification(`Failed to create case: ${error}`)
    }
  }

  return (
    <Modal
      title='Create Case'
      open={isOpen}
      onCancel={onClose}
      width={900}
      footer={
        <PermissionsGate requires={[PERMISSION_CANTILEVER_CASES_EDITOR]}>
          <CantileverButton primary onClick={handleSaveCase}>
            Create Case
          </CantileverButton>
        </PermissionsGate>
      }>
      <ModalContent>
        <FormContainer>
          <CreateCaseForm
            formFields={caseFormFields}
            inEditMode={true}
            ref={caseFormRef}
            onSubmit={onSubmit}
          />
        </FormContainer>
        <EditorSection>
          <EditorLabel>Description</EditorLabel>
          <EditorContainer>
            <RichTextEditorLexical
              value={content}
              onChange={setContent}
              placeholder='Describe the issue...'
              showToolbar={true}
              mentionPlugin={<MentionPlugin />}
              enableSlashCommands={true}
              entryIdForImages={caseID}
            />
          </EditorContainer>
        </EditorSection>
      </ModalContent>
    </Modal>
  )
}
